import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { InfinityLoop, UnitedFigures, HouseHeart, SmileFigure } from "../components/Illustrations";
import { HandHeart, MessagesSquare, Compass, Sparkles } from "lucide-react";

const VALUES = [
  { icon: HandHeart, title: "People first, always", body: "Every CV is a person. Every brief is a team. We never forget that." },
  { icon: MessagesSquare, title: "Honest conversations", body: "If a role isn't right, we'll say so. Same with a candidate. It saves everyone time." },
  { icon: Compass, title: "Genuine partnership", body: "We're not transactional. We invest in long relationships on both sides." },
  { icon: Sparkles, title: "High craft", body: "Carefully written briefs. Carefully prepared candidates. Carefully matched introductions." },
];

export default function About() {
  return (
    <div data-testid="page-about">
      <Seo
        title="About"
        description="Founded in Aotearoa. Built on conversations, not algorithms. Meet the boutique technology recruitment partner that takes time to understand both sides."
        image="/og-brand.png"
        path="/about"
      />
      <PageHero
        overline="About Unite Recruitment"
        title="Technology recruitment that remembers it's about people."
        accentWord="people"
        subtitle="A boutique New Zealand technology recruitment partner built on the belief that great hires happen when both sides feel genuinely understood."
      />

      {/* Story */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-5">
            <div className="aspect-square rounded-2xl bg-stone flex items-center justify-center p-12">
              <UnitedFigures className="w-full h-full" stroke="#C2FFE1" strokeWidth={5} />
            </div>
          </div>
          <div className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-5">Our approach</p>
            <h2 className="font-headings text-4xl md:text-5xl text-ink tracking-tight leading-tight mb-6">
              Built on conversations, not algorithms.
            </h2>
            <div className="space-y-5 font-body text-ink-700 text-lg leading-relaxed">
              <p>
                We think a good hire — for both sides — starts with a proper
                conversation. Not a keyword filter, not a mass mail-out, not a
                long-list to work through on your lunch break.
              </p>
              <p>
                So we do things a little differently. We take time to meet
                candidates properly, to brief on the role and the team, and to
                only put forward people we'd genuinely be happy to hire
                ourselves.
              </p>
              <p>It's slower in the short term. It's better in every other way.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How we work */}
      <section className="py-20 md:py-32 bg-mint-soft" data-testid="about-how-we-work">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-5">How we work</p>
            <h2 className="font-headings text-4xl md:text-5xl text-ink tracking-tight leading-tight">
              The same principle, both sides of the table.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-white p-8 md:p-10 rounded-2xl border border-ink-100 relative overflow-hidden">
              <SmileFigure className="absolute -right-6 -top-2 w-36 h-44 opacity-80" stroke="#9CE8C2" strokeWidth={4} />
              <p className="text-xs uppercase tracking-[0.25em] text-stone-dark mb-3 relative">With candidates</p>
              <h3 className="font-headings text-2xl md:text-3xl text-ink mb-4 leading-tight relative">
                We listen first. Then we open doors.
              </h3>
              <p className="font-body text-ink-700 leading-relaxed relative">
                A proper conversation about what you actually want — money,
                growth, lifestyle, team — before we put your name anywhere.
              </p>
              <Link
                to="/find-a-job"
                data-testid="about-cta-find-job"
                className="inline-flex mt-6 text-ink font-medium link-underline relative"
              >
                Take me to Find a Job →
              </Link>
            </div>

            <div className="bg-ink text-cream p-8 md:p-10 rounded-2xl relative overflow-hidden">
              <HouseHeart className="absolute -right-8 -bottom-2 w-72 h-32 opacity-80" stroke="#C2FFE1" strokeWidth={4} />
              <p className="text-xs uppercase tracking-[0.25em] text-mint mb-3 relative">With employers</p>
              <h3 className="font-headings text-2xl md:text-3xl mb-4 leading-tight relative">
                We brief deeply. Then we shortlist tightly.
              </h3>
              <p className="font-body text-cream/85 leading-relaxed relative">
                Three to five candidates we'd genuinely hire ourselves — not a
                wall of names you have to filter.
              </p>
              <Link
                to="/find-a-candidate"
                data-testid="about-cta-find-candidate"
                className="inline-flex mt-6 text-mint font-medium link-underline relative"
              >
                Take me to Find a Candidate →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-5">What we stand for</p>
            <h2 className="font-headings text-4xl md:text-5xl text-ink tracking-tight leading-tight">
              Four values, no committee-speak.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div
                  key={i}
                  data-testid={`value-${i}`}
                  className="bg-mint p-8 rounded-2xl flex flex-col h-full"
                >
                  <Icon size={28} className="text-ink mb-6" strokeWidth={1.5} />
                  <h3 className="font-headings text-xl text-ink mb-3 leading-tight">{v.title}</h3>
                  <p className="font-body text-ink-700 text-sm leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="bg-stone text-cream rounded-2xl p-10 md:p-16 flex flex-col md:flex-row md:items-center md:justify-between gap-8 relative overflow-hidden">
            <InfinityLoop className="absolute -right-10 -top-6 w-96 h-32 opacity-30" stroke="#C2FFE1" strokeWidth={4} />
            <div className="relative">
              <h3 className="font-headings text-3xl md:text-4xl leading-tight">
                Ready to start a real conversation?
              </h3>
              <p className="mt-3 font-body text-cream/75 max-w-lg">
                Whichever side of the table you're on, we'd love to hear from you.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="inline-flex items-center gap-2 bg-mint hover:bg-mint-deep text-ink px-8 py-4 rounded-full font-body font-medium whitespace-nowrap relative"
            >
              Contact us →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
